import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TABLE_PAGE_SIZE } from '@core/constants/app.constants';

/**
 * Displays placeholder table rows while list data is loading.
 */
@Component({
  selector: 'app-table-skeleton',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table-skeleton.component.html',
  styleUrl: './table-skeleton.component.css',
})
export class TableSkeletonComponent {
  /** Number of skeleton rows to render. Defaults to the global table page size. */
  readonly rows = input<number>(TABLE_PAGE_SIZE);

  /** Total number of columns in the table. */
  readonly cols = input.required<number>();

  /** Placeholder row indexes used by the template. */
  readonly rowArray  = computed(() => Array.from({ length: this.rows() }));
  /** Placeholder column indexes used by the template. */
  readonly colArray  = computed(() => Array.from({ length: this.cols() }));
  /** Columns after the first (which always gets the icon+text treatment). */
  readonly restCols  = computed(() => Array.from({ length: Math.max(0, this.cols() - 1) }));

  /**
   * Cycles through widths to make bars look naturally varied.
   *
   * @param colIndex Column index within the skeleton row.
   * @param rowIndex Row index within the skeleton table.
   * @returns CSS width value for the placeholder bar.
   */
  barWidth(colIndex: number, rowIndex: number): string {
    const widths = ['55%', '75%', '60%', '85%', '50%', '70%'];
    return widths[(colIndex + rowIndex) % widths.length];
  }

  /**
   * Checks whether a rendered skeleton cell is the final visible data column.
   *
   * @param colIndex Column index inside `restCols`.
   * @returns Whether the cell should render action circles instead of a bar.
   */
  isLastCol(colIndex: number): boolean {
    return colIndex === this.cols() - 2; // -2 because restCols starts at index 1
  }
}
